namespace $ {

	export type $shm_hitalama_map_method_decorator = $shm_hitalama_method_decorator
	export const $shm_hitalama_map_method_decorator = $shm_hitalama_method_decorator
	export const $shm_hitalama_map_method_define = $shm_hitalama_method_define
	export class $shm_hitalama_map_method_app extends $shm_hitalama_method_app {}

	export class $shm_hitalama_map_method_runtime extends $shm_hitalama_method_runtime {

		import_layout_top = 40
		import_widgets = null as null | Map< string, $shm_hitalama_map_widget >

		map() {
			return null as any as $shm_hitalama_map
		}

		@ $mol_mem
		widgets() {
			return this.map().Widgets()?.remote()?.remote_list()
				.filter( widget => widget.type() === 'method' )
				?? []
		}

		methods(): readonly $shm_hitalama_method_def[] {
			return this.widgets().map( widget => ({
				name: widget.title(),
				code: widget.code(),
				decorator: widget.data()?.decorator,
				args: widget.data()?.args,
				source: widget,
			}) )
		}

		value( key: string, next?: any ) {
			return this.map().store( key, next )
		}

		method_import_start( methods: readonly $shm_hitalama_method_def[] ) {
			this.import_layout_top = this.method_import_layout_top()
			this.import_widgets = this.method_widgets_by_name()
		}

		method_import_end() {
			this.import_layout_top = 40
			this.import_widgets = null
		}

		method_widgets_by_name() {
			const widgets = this.map().Widgets()?.remote()?.remote_list()
				.filter( widget => widget.type() === 'method' )
				?? []

			return new Map( widgets.map( widget => [ widget.title(), widget ] ) )
		}

		method_widget_by_name( name: string ) {
			return this.method_widgets_by_name().get( name ) ?? null
		}

		method_widget_links( method_widget: $shm_hitalama_map_widget ) {
			const refs = new Set< string >
			const methods = this.method_widgets_by_name()
			const code = method_widget.code()

			methods.forEach( ( widget, name )=> {
				if( widget.link_str() == method_widget.link_str() ) return

				const escaped = this.method_name_escape( name )
				const regex = new RegExp( `\\bthis\\s*\\.\\s*${ escaped }\\s*\\(` )
				if( regex.test( code ) ) refs.add( widget.link_str() )
			} )

			return [ ...refs ]
		}

		method_widget_left( widget: $shm_hitalama_map_widget ) {
			return widget.body_x() + ( widget.Left_edge_x()?.val() ?? 0 )
		}

		method_widget_top( widget: $shm_hitalama_map_widget ) {
			return widget.body_y() + ( widget.Top_edge_y()?.val() ?? 0 )
		}

		method_widget_width( widget: $shm_hitalama_map_widget ) {
			return Math.max( 1, widget.right_edge_x() - ( widget.Left_edge_x()?.val() ?? 0 ) )
		}

		method_widget_height( widget: $shm_hitalama_map_widget ) {
			return Math.max( 1, widget.bottom_edge_y() - ( widget.Top_edge_y()?.val() ?? 0 ) )
		}

		method_widget_place( widget: $shm_hitalama_map_widget, left: number, top: number ) {
			widget.body_x( left - ( widget.Left_edge_x()?.val() ?? 0 ) )
			widget.body_y( top - ( widget.Top_edge_y()?.val() ?? 0 ) )
		}

		method_layout_layers(
			widgets: readonly $shm_hitalama_map_widget[],
			parents: ReadonlyMap< string, readonly string[] >,
		) {
			const by_link = new Map( widgets.map( widget => [ widget.link_str(), widget ] ) )
			const depths = new Map< string, number >
			const visiting = new Set< string >

			const depth = ( link: string ): number => {
				const cached = depths.get( link )
				if( cached !== undefined ) return cached
				if( visiting.has( link ) ) return 0

				visiting.add( link )
				const value = Math.max(
					0,
					... ( parents.get( link ) ?? [] )
						.filter( parent => by_link.has( parent ) )
						.map( parent => depth( parent ) + 1 ),
				)
				visiting.delete( link )
				depths.set( link, value )
				return value
			}

			widgets.forEach( widget => depth( widget.link_str() ) )

			const layers = [] as $shm_hitalama_map_widget[][]
			widgets.forEach( widget => {
				const index = depths.get( widget.link_str() ) ?? 0
				if( !layers[ index ] ) layers[ index ] = []
				layers[ index ].push( widget )
			} )

			layers.forEach( layer => layer.sort( ( left, right )=> {
				const top_delta = this.method_widget_top( left ) - this.method_widget_top( right )
				return top_delta || left.title().localeCompare( right.title() )
			} ) )

			return layers
		}

		method_layout_order(
			layers: $shm_hitalama_map_widget[][],
			parents: ReadonlyMap< string, readonly string[] >,
			children: ReadonlyMap< string, readonly string[] >,
		) {
			const index = new Map< string, number >
			const refresh = ()=> layers.forEach( layer =>
				layer.forEach( ( widget, pos )=> index.set( widget.link_str(), pos ) )
			)
			const weight = ( links: readonly string[] )=> {
				const positions = links.flatMap( link => {
					const pos = index.get( link )
					return pos === undefined ? [] : [ pos ]
				} )
				if( !positions.length ) return null
				return positions.reduce( ( sum, pos )=> sum + pos, 0 ) / positions.length
			}
			const sort = ( layer: $shm_hitalama_map_widget[], links: ReadonlyMap< string, readonly string[] > )=> {
				layer.sort( ( left, right )=> {
					const left_weight = weight( links.get( left.link_str() ) ?? [] )
					const right_weight = weight( links.get( right.link_str() ) ?? [] )
					if( left_weight === null && right_weight === null ) return 0
					if( left_weight === null ) return 1
					if( right_weight === null ) return -1
					return left_weight - right_weight
				} )
			}

			for( let pass = 0; pass < 6; ++pass ) {
				refresh()
				for( let i = 1; i < layers.length; ++i ) sort( layers[i], parents )
				refresh()
				for( let i = layers.length - 2; i >= 0; --i ) sort( layers[i], children )
			}
		}

		@ $mol_action
		method_widgets_layout() {
			const widgets = this.widgets().filter( widget => !this.map().widget_container_actual( widget ) )
			if( widgets.length === 0 ) return []

			const visible = new Set( widgets.map( widget => widget.link_str() ) )
			const parents = new Map< string, string[] >
			const children = new Map< string, string[] >

			widgets.forEach( widget => {
				const link = widget.link_str()
				const deps = this.method_widget_links( widget ).filter( ref => visible.has( ref ) )
				parents.set( link, deps )

				deps.forEach( dep => {
					const list = children.get( dep ) ?? []
					list.push( link )
					children.set( dep, list )
				} )
			} )

			const layers = this.method_layout_layers( widgets, parents )
			this.method_layout_order( layers, parents, children )

			const gap_x = 120
			const gap_y = 48
			const left = Math.min( ... widgets.map( widget => this.method_widget_left( widget ) ), 40 )
			const top = Math.min( ... widgets.map( widget => this.method_widget_top( widget ) ), 40 )
			const layer_heights = layers.map( layer =>
				layer.reduce( ( sum, widget )=> sum + this.method_widget_height( widget ), 0 )
				+ Math.max( 0, layer.length - 1 ) * gap_y
			)
			const max_height = Math.max( ... layer_heights )

			let x = left
			layers.forEach( ( layer, layer_index )=> {
				const width = Math.max( ... layer.map( widget => this.method_widget_width( widget ) ) )
				let y = top + ( max_height - layer_heights[ layer_index ] ) / 2

				layer.forEach( widget => {
					this.method_widget_place( widget, Math.round( x ), Math.round( y ) )
					y += this.method_widget_height( widget ) + gap_y
				} )

				x += width + gap_x
			} )

			return widgets
		}

		method_import_layout_top() {
			const widgets = this.map().Widgets()?.remote()?.remote_list() ?? []
			const bottom = Math.max(
				0,
				... widgets.map( widget =>
					widget.body_y() + widget.bottom_edge_y()
				),
			)
			return bottom + 40
		}

		method_import_pos( index: number ) {
			const width = 600
			const height = 400
			const gap = 24
			const cols = 3
			const col = index % cols
			const row = Math.floor( index / cols )
			return [
				40 + col * ( width + gap ),
				this.import_layout_top + row * ( height + gap ),
			] as const
		}

		@ $mol_action
		method_save( method: $shm_hitalama_method_def, index = 0 ) {
			const widget = this.method_widget( method, index )
			widget.title( method.name )
			widget.code( method.code )
			widget.data({
				... widget.data(),
				args: method.args,
				decorator: this.$.$shm_hitalama_method_decorator( method.decorator ),
			})
		}

		method_widget( method: $shm_hitalama_method_def, index = 0 ) {
			if( method.source instanceof $shm_hitalama_map_widget ) return method.source

			const widgets = this.import_widgets ?? this.method_widgets_by_name()
			const exists = widgets.get( method.name )
			if( exists ) return exists

			const widget = this.map().widget_add( 'method', this.method_import_pos( index ), 600, 400, method.name )!
			widget.auto_height( true )
			widgets.set( method.name, widget )
			return widget
		}

	}

}
